import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import { useCSVDownloader } from 'react-papaparse';
import { Message, Button, Popup, Dropdown } from 'semantic-ui-react';
import { runWithConfirmation } from '../../utils/useful_functions';
import { store } from '../../store';
import additionalMessages from '../../messages';
import { uiConfirmAction } from '../mainframe/mainframe.reducer';
import AttachSubscribersProject from './AttachSubscribersDialog';
import { getWithAuth, postWithAuth } from 'src/api';
import { validateSubscriberSites } from 'src/utils/mapUtils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSubscriberColumns } from 'src/components/tables/internal/useSubscriberColumns';
import { useLpTable } from 'src/components/tables/internal/useLpTable';
import { LPTable } from 'src/components/tables/LPTable';
import { toast } from 'react-toastify';
import sortBy from 'lodash/sortBy';
import {
  removeLinkSuffix,
  getCSVData,
} from 'src/components/tables/ColumnUtils';
import produce from 'immer';
import { GREY_COLOR, TABLE_MAX_ROWS } from 'src/app.constants';
import { createPMPLinkTableQueryKey } from '../../components/tables/internal/TableQueryKeys';

function PMPListPanel(props) {
  const { formatMessage } = props.intl;
  const { CSVDownloader } = useCSVDownloader();
  const [showAttachSM, setShowAttachSM] = useState(false);
  const {
    projectId,
    projectName,
    accessPointsCount,
    subscriberSiteCount,
    pmpLinksCount,
    userLimits,
    permissionWrite,
  } = useSelector((state) => state.mainFrame);

  let queryKey = createPMPLinkTableQueryKey(projectId);
  let url = `project/${projectId}/pmp_links`;
  if (props.apId) {
    url = `project/${projectId}/access_point/${props.apId}/subscribers`;
    queryKey = [
      'project',
      projectId,
      'access_point',
      props.apId,
      'subscribers',
    ];
  }
  const { data: tableData } = useQuery({
    queryKey,
    initialData: { rows: [], visible: [] },
    queryFn: () => getWithAuth(url),
  });

  const pinnedColumn = props.defaultPinnedColumn ?? 'name';
  const pageSize = props.pageSize ?? TABLE_MAX_ROWS;
  const { columns: allColumns } = useSubscriberColumns(pinnedColumn);
  // hide ND column of PMP links table in ND details page.
  // Memoized deliberately: useLpTable derives column visibility and order from
  // this array, so a fresh array on every render made the effect there
  // overwrite the user's Choose Columns selection with the (stale) list the
  // API last returned.
  const columns = React.useMemo(
    () =>
      props.apId
        ? allColumns.filter((col) => col.id !== 'ap_name')
        : allColumns,
    [allColumns, props.apId]
  );
  const tableModel = useLpTable({
    data: tableData.rows,
    visibleColumns: tableData.visible,
    columns,
    getRowId: (row) => `${row.id}-${row.path_index}`,
    options: {
      defaultPinnedColumn: pinnedColumn,
      pageSize,
      tableId: props.tableId || 'pmp_links_table',
    },
  });

  const deleteSelection = useMutation({
    mutationFn: () => {
      const selectedRows = tableModel.table
        .getSelectedRowModel()
        .rows.map((row) => row.original.id);

      if (selectedRows.length === 0) {
        return Promise.reject();
      }

      return postWithAuth(
        `project/${projectId}/pmp_links`,
        selectedRows,
        'DELETE'
      );
    },
    onError: () => {
      toast(<Message error>Failed to delete PMP Links</Message>);
    },
  });

  const deleteHandler = (formatMessage) => {
    store.dispatch(
      uiConfirmAction({
        header: formatMessage(additionalMessages.deletePMPLinks),
        message: formatMessage(additionalMessages.confirm),
        size: 'mini',
        onConfirm: () => {
          deleteSelection.mutate();
        },
      })
    );
  };

  let addWarning = '';
  if (pmpLinksCount >= userLimits?.pmp_links) {
    addWarning = formatMessage(additionalMessages.maximumNumberError, {
      entityName: 'PMP link',
      limit: userLimits?.pmp_links,
    });
  }

  const enableAdd =
    permissionWrite &&
    addWarning === '' &&
    accessPointsCount > 0 &&
    subscriberSiteCount > 0;

  const enableDelete =
    permissionWrite && tableModel.table.getSelectedRowModel().rows.length > 0;

  return (
    <>
      {showAttachSM && (
        <AttachSubscribersProject
          intl={props.intl}
          apId={props.apId}
          onClose={() => setShowAttachSM(false)}
          validateSubscriberSites={validateSubscriberSites}
        ></AttachSubscribersProject>
      )}

      {addWarning && <Message warning>{addWarning}</Message>}

      <LPTable
        queryKey={queryKey}
        model={tableModel}
        tableId={props.tableId || 'pmp_links_table'}
      >
        <LPTable.Toolbar>
          <Button
            icon="add"
            type="button"
            content={formatMessage(additionalMessages.add)}
            disabled={!enableAdd}
            onClick={() => setShowAttachSM(true)}
          />

          <Button
            icon="trash alternate"
            type="button"
            content={formatMessage(additionalMessages.delete)}
            disabled={!enableDelete}
            onClick={() => {
              deleteHandler(formatMessage);
            }}
          />

          <CSVDownloader
            filename={`${projectName}_pmp_links`}
            bom={true}
            config={{ delimiter: ',' }}
            data={() => getCSVData(tableModel.table)}
          >
            <Button
              icon="cloud download"
              content="Download CSV"
              type="button"
              disabled={tableModel.table.getRowCount() === 0}
            />
          </CSVDownloader>

          <CopyFrom
            formatMessage={formatMessage}
            rowData={tableData.rows}
            tableModel={tableModel}
            queryKey={queryKey}
          />
        </LPTable.Toolbar>
      </LPTable>
    </>
  );
}

function CopyFrom(props) {
  const { formatMessage, rowData, tableModel, queryKey } = props;

  const { projectId, permissionWrite } = useSelector(
    (state) => state.mainFrame
  );

  const qc = useQueryClient();

  return (
    <Popup
      content={formatMessage(additionalMessages.copyFromHelp, {
        kind: 'pmp link',
      })}
      position="right center"
      trigger={
        <Dropdown
          button
          fluid
          search
          selection
          labeled
          icon="paste"
          text={formatMessage(additionalMessages.dropdownPlaceHolderTxt)}
          disabled={
            !permissionWrite ||
            tableModel.table.getSelectedRowModel().rows.length === 0
          }
          className="icon sm-nd-dropdown-options"
          style={{ margin: '0' }}
          options={sortBy(
            rowData
              .filter((row) => {
                const selected = tableModel.table
                  .getSelectedRowModel()
                  .rows.map((r) => r.original.remote_end_id);
                return (
                  !selected.includes(row.remote_end_id) && row.path_index === 0
                );
              })
              .map((row) => ({
                key: row.remote_end_id,
                text: removeLinkSuffix(row.name),
                value: [row.remote_end_id, removeLinkSuffix(row.name)],
              })),
            (opt) => opt.text
          )}
          onChange={(_, data) => {
            const rows = tableModel.table.getSelectedRowModel().rows;
            const ids = [
              ...new Set(
                rows
                  .filter((row) => row.original.path_index === 0)
                  .map((row) => row.original.remote_end_id)
              ),
            ];

            if (ids.length < 1) {
              return;
            }

            const [copyId, selectedName] = data.value;
            const qtyAndKind =
              rows.length === 1
                ? 'selected pmp link'
                : `${rows.length} selected pmp links`;

            runWithConfirmation({
              message: `${formatMessage(
                additionalMessages.copyFromConfirmation,
                { qtyAndKind }
              )} ${selectedName}`,
              onConfirm: () => {
                const payload = {
                  ids,
                  copy_id: copyId,
                  data: {},
                  bulk: 'copy',
                };

                postWithAuth(
                  `project/${projectId}/subscribers/bulk`,
                  payload,
                  'PATCH'
                ).then(() => {
                  qc.setQueryData(queryKey, (prev) => {
                    return produce(prev, (draft) => {
                      for (const row of draft.rows) {
                        if (ids.includes(row.remote_end_id)) {
                          row.strokeColor = GREY_COLOR;
                        }
                      }
                    });
                  });
                });
              },
            });
          }}
        />
      }
    />
  );
}

export default injectIntl(PMPListPanel);
